import { useMemo, useState } from 'react';
import axios from 'axios';
import {
  ArrowRight,
  Bookmark,
  BriefcaseBusiness,
  Building2,
  Check,
  Copy,
  Heart,
  HeartCrack,
  HelpCircle,
  MoreHorizontal,
  RefreshCw,
  Shuffle,
  Sparkles,
  Users,
} from 'lucide-react';
import './VibeV2.css';
import VibeStoryTour from './VibeStoryTour';

const LOCAL_API_ORIGIN = ['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'https://vibes.terencela.com' : '';
const API = `${process.env.REACT_APP_BACKEND_URL || LOCAL_API_ORIGIN}/api`;
const EMAIL_STORAGE_KEY = 'vibeEmailV1';
const PROFILES_STORAGE_KEY = 'vibeV2RecipientProfilesV1';
const TOUR_SESSION_KEY = 'vibeStoryTourSeenV1';
const MAX_MESSAGE_LENGTH = 400;

const PRESETS = [
  { id: 'dating', label: 'Dating', icon: Heart, defaultName: 'My date' },
  { id: 'boss', label: 'Boss', icon: BriefcaseBusiness, defaultName: 'My boss' },
  { id: 'friend', label: 'Friend', icon: Users, defaultName: 'My friend' },
  { id: 'client', label: 'Client', icon: Building2, defaultName: 'My client' },
  { id: 'ex', label: 'Ex', icon: HeartCrack, defaultName: 'My ex' },
  { id: 'custom', label: 'Custom', icon: MoreHorizontal, defaultName: '' },
];

const EXAMPLES = [
  "It's fine, do whatever you want.",
  "I'm not mad.",
  "K.",
  "Have fun.",
  "You don't have to.",
];

function normalizeEmail(value) {
  return typeof value === 'string'
    ? value.replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase()
    : '';
}

function plausibleEmail(value) {
  const email = normalizeEmail(value);
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function readSavedProfiles() {
  try {
    const value = JSON.parse(localStorage.getItem(PROFILES_STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.slice(0, 12) : [];
  } catch {
    return [];
  }
}

function EmailGate({ onUnlock }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const clean = normalizeEmail(email);
    if (!plausibleEmail(clean)) {
      setError('Enter a valid email address.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await axios.post(`${API}/submit-email`, { email: clean });
      if (!response.data?.persisted) throw new Error('Email was not saved');
      localStorage.setItem(EMAIL_STORAGE_KEY, clean);
      onUnlock(clean);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Could not save your email. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="v2-gate">
      <form className="v2-gate-card" onSubmit={submit} noValidate>
        <div className="v2-gate-kicker">VIBE TRANSLATOR</div>
        <h1>READ THE ROOM.</h1>
        <p>Enter your email. No password, no verification.</p>
        <label htmlFor="v2-email">Email address</label>
        <input
          id="v2-email"
          data-testid="v2-email-input"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setError('');
          }}
          placeholder="you@example.com"
          autoFocus
        />
        <p className="v2-form-error" role="alert">{error}</p>
        <button type="submit" disabled={busy} data-testid="v2-email-submit">
          {busy ? 'SAVING...' : 'START READING'} <ArrowRight aria-hidden="true" />
        </button>
        <small>Your email and chats are stored for this beta.</small>
      </form>
    </main>
  );
}

function VibeV2Product({ email, onChangeEmail }) {
  const [relationship, setRelationship] = useState('dating');
  const [name, setName] = useState('Sarah');
  const [context, setContext] = useState('Dating for 3 months. Knows me well.');
  const [message, setMessage] = useState("It's fine, do whatever you want.");
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState('');
  const [savedProfiles, setSavedProfiles] = useState(readSavedProfiles);
  const [showHow, setShowHow] = useState(() => {
    try {
      return sessionStorage.getItem(TOUR_SESSION_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const [showContext, setShowContext] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);

  const initials = useMemo(() => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return (parts.slice(0, 2).map((part) => part[0]).join('') || '?').toUpperCase();
  }, [name]);

  const choosePreset = (preset) => {
    setRelationship(preset.id);
    if (preset.id !== 'custom') setName(preset.defaultName);
    setResult(null);
    setError('');
  };

  const closeTour = () => {
    try { sessionStorage.setItem(TOUR_SESSION_KEY, '1'); } catch { /* Session storage can be unavailable. */ }
    setShowHow(false);
  };

  const analyze = async ({ alternate = false } = {}) => {
    if (!name.trim()) {
      setError("Add the recipient's name.");
      return;
    }
    if (!message.trim()) {
      setError('Paste a message to decode.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/v2/analyze`, {
        email,
        message: message.trim(),
        recipient: { name: name.trim(), relationship, context: context.trim() },
        alternate,
        previousReading: alternate ? result?.reading : '',
      });
      setResult(response.data);
      setTimeout(() => document.getElementById('v2-result')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 40);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Could not read that vibe. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = () => {
    const cleanProfile = { id: `${relationship}:${name.trim().toLowerCase()}`, name: name.trim(), relationship, context: context.trim() };
    const next = [cleanProfile, ...savedProfiles.filter((item) => item.id !== cleanProfile.id)].slice(0, 12);
    setSavedProfiles(next);
    localStorage.setItem(PROFILES_STORAGE_KEY, JSON.stringify(next));
    setSavedNotice(true);
    window.setTimeout(() => setSavedNotice(false), 1800);
  };

  const loadProfile = (profile) => {
    setName(profile.name);
    setRelationship(profile.relationship);
    setContext(profile.context || '');
    setResult(null);
  };

  const copyReply = async (text, index) => {
    await navigator.clipboard.writeText(text);
    setCopied(index);
    window.setTimeout(() => setCopied(''), 1400);
  };

  return (
    <main className="v2-shell">
      <header className="v2-hero">
        <div className="v2-title-block">
          <h1>VIBE<br />TRANSLATOR</h1>
          <p>What did they <strong>actually</strong> mean?</p>
        </div>
        <div className="v2-brand-stamp">
          <span>FR FR</span>
          <button type="button" onClick={() => setShowHow(true)}>
            <HelpCircle aria-hidden="true" /> HOW IT WORKS
          </button>
        </div>
      </header>

      <section className="v2-workspace" aria-label="Vibe translator">
        <button type="button" className="v2-context-summary" onClick={() => setShowContext((value) => !value)} aria-expanded={showContext}>
          <div className="v2-context-avatar" aria-hidden="true">{initials}</div>
          <div>
            <span>WHO SAID IT?</span>
            <strong>{name || 'Add a person'}</strong>
            <small>{relationship}{context ? ` · ${context}` : ''}</small>
          </div>
          <b>{showContext ? 'DONE' : 'EDIT'}</b>
        </button>

        <div className={`v2-person-row ${showContext ? 'is-open' : ''}`}>
          <div className="v2-person-editor">
            <h2>WHO SAID IT?</h2>
            <div className="v2-person-card">
              <div className="v2-initials" aria-hidden="true">{initials}</div>
              <div className="v2-person-fields">
                <label htmlFor="v2-name">Their name</label>
                <input id="v2-name" value={name} onChange={(event) => setName(event.target.value.slice(0, 60))} />
                <label htmlFor="v2-context">What matters here?</label>
                <input
                  id="v2-context"
                  value={context}
                  onChange={(event) => setContext(event.target.value.slice(0, 240))}
                  placeholder="How long, how close, recent tension..."
                />
              </div>
            </div>
          </div>

          <div className="v2-presets">
            <h2>QUICK PRESETS</h2>
            <div className="v2-preset-grid">
              {PRESETS.map((preset) => {
                const Icon = preset.icon;
                return (
                  <button
                    type="button"
                    key={preset.id}
                    className={relationship === preset.id ? 'is-active' : ''}
                    onClick={() => choosePreset(preset)}
                    aria-pressed={relationship === preset.id}
                  >
                    <Icon aria-hidden="true" />
                    <span>{preset.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {savedProfiles.length > 0 && (
          <div className="v2-saved-people" aria-label="Saved people">
            <span>SAVED:</span>
            {savedProfiles.map((profile) => (
              <button type="button" key={profile.id} onClick={() => loadProfile(profile)}>{profile.name}</button>
            ))}
          </div>
        )}

        <div className="v2-message-block">
          <label htmlFor="v2-message">THE MESSAGE</label>
          <textarea
            id="v2-message"
            data-testid="v2-message"
            value={message}
            maxLength={MAX_MESSAGE_LENGTH}
            onChange={(event) => {
              setMessage(event.target.value);
              setError('');
            }}
            placeholder="Paste exactly what they sent..."
          />
          <span className="v2-count">{message.length}/{MAX_MESSAGE_LENGTH}</span>
        </div>

        <button
          type="button"
          className="v2-primary-action"
          data-testid="v2-analyze"
          onClick={() => analyze()}
          disabled={loading}
        >
          <Sparkles aria-hidden="true" /> {loading ? 'READING THE ROOM...' : 'WHAT DID THEY MEAN?'}
        </button>

        {error && <p className="v2-error" role="alert">{error}</p>}

        <section id="v2-result" className={`v2-result ${loading ? 'is-loading' : ''}`} aria-live="polite">
          {loading ? (
            <div className="v2-result-loading">
              <span />
              <span />
              <span />
              <p>Reading the words, the context, and what is left unsaid.</p>
            </div>
          ) : result ? (
            <>
              <article className="v2-reading">
                <div className="v2-result-heading">
                  <h2>THE LIKELY READ</h2>
                  <button type="button" onClick={() => analyze({ alternate: true })}>
                    ANOTHER TAKE <RefreshCw aria-hidden="true" />
                  </button>
                </div>
                <p className="v2-reading-copy">{result.reading}</p>
                <div className="v2-reasons">
                  <h3>Why this makes sense</h3>
                  <ul>{result.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
                </div>
                {result.alternateRead && (
                  <p className="v2-alternate"><strong>Also possible:</strong> {result.alternateRead}</p>
                )}
              </article>

              <aside className="v2-replies">
                <h2>WHAT SHOULD I SAY?</h2>
                <div className="v2-reply-list">
                  {result.replyIdeas.map((reply, index) => (
                    <button type="button" key={`${reply.label}-${index}`} onClick={() => copyReply(reply.text, index)}>
                      <span>{reply.label}</span>
                      <p>{reply.text}</p>
                      <b>{copied === index ? <Check aria-label="Copied" /> : <Copy aria-label="Copy reply" />}</b>
                    </button>
                  ))}
                </div>
              </aside>

              <div className="v2-save-row">
                <Bookmark aria-hidden="true" />
                <div>
                  <strong>Save {name || 'this person'} to remember</strong>
                  <span>Stored on this device. Used only when you choose this person.</span>
                </div>
                <button type="button" onClick={saveProfile}>{savedNotice ? 'SAVED' : 'SAVE'}</button>
              </div>
            </>
          ) : (
            <div className="v2-empty-result">
              <Sparkles aria-hidden="true" />
              <h2>THE WORDS ARE ONLY HALF THE MESSAGE.</h2>
              <p>Choose who said it, paste the exact text, then read the likely meaning and your best next move.</p>
            </div>
          )}
        </section>

        <div className="v2-examples">
          <span>TRY ANOTHER MESSAGE</span>
          <div>
            {EXAMPLES.map((example) => (
              <button type="button" key={example} onClick={() => { setMessage(example); setResult(null); }}>{example}</button>
            ))}
            <button
              type="button"
              className="v2-surprise"
              onClick={() => setMessage(EXAMPLES[Math.floor(Math.random() * EXAMPLES.length)])}
            >
              SURPRISE ME <Shuffle aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      <footer className="v2-footer">
        <span>NO CAP, FR FR. BUILT FOR THE MEMES, MADE FOR REAL LIFE.</span>
        <button type="button" onClick={onChangeEmail}>{email}</button>
        <a href="https://www.linkedin.com/in/terencela" target="_blank" rel="noreferrer">TERENCE LA</a>
      </footer>

      {showHow && <VibeStoryTour onClose={closeTour} />}
    </main>
  );
}

export default function VibeV2() {
  const [email, setEmail] = useState(() => {
    const isLocalPreview = ['localhost', '127.0.0.1'].includes(window.location.hostname)
      && new URLSearchParams(window.location.search).get('preview') === '1';
    if (isLocalPreview) return 'preview@local.invalid';
    try {
      const stored = localStorage.getItem(EMAIL_STORAGE_KEY) || '';
      return plausibleEmail(stored) ? normalizeEmail(stored) : '';
    } catch {
      return '';
    }
  });

  if (!email) return <EmailGate onUnlock={setEmail} />;
  return <VibeV2Product email={email} onChangeEmail={() => { localStorage.removeItem(EMAIL_STORAGE_KEY); setEmail(''); }} />;
}
